import {
  ColumnDataTypes,
  FilterOperators,
  FilterOptionTypes,
  type ColumnConfig,
} from '@/components/DataTable';
import type { HookInventoryRow } from '@kirin/api-client/contracts/hooks';
import { HOOK_FILTERABLE_FIELDS } from '@kirin/api-client/contracts/hooks';
import {
  TypeCell,
  PlatformCell,
  FileCell,
  SummaryCell,
  ProjectsCell,
  UsersCell,
  LastSeenCell,
} from './CellRenderers';
import {
  FileCategoryFilterOption,
  PlatformFilterOption,
  ProjectFilterOption,
  FilePathFilterOption,
} from './FilterComponents';
import { HOOK_FILTER_URL_KEYS } from './constants';
import {
  fetchHookFilterOptions,
  getHookFilterOptionsQueryKey,
  fetchHookProjectFilterOptions,
  getHookProjectFilterOptionsQueryKey,
} from './api';
import { getHookFileDisplayLabel } from './fileLabel';
import type { HookTabScope } from './scope';

export interface HookColumnHandlers {
  onProjectsClick: (groupKey: string) => void;
  onUsersClick: (groupKey: string) => void;
}

/**
 * Column set for the Hooks inventory DataTable (KIR-3643):
 * Type + Event | IDE/Platform | File | Hook Details | Users | Last Seen |
 * Projects. Every filterable column is wired to the backend filter fields
 * (column `id` === the backend filter field): family/platform/file/projects use
 * server-backed dynamic dropdowns; summary is a free-text filter.
 * family/platform stay dynamic on purpose — new platforms add new hook
 * families, so we never hand-maintain an option list. `scope` (org vs personal)
 * threads through the filter-option fetchers so the same columns serve both the
 * organization and personal Hooks views.
 */
export function createHookColumns(
  scope: HookTabScope,
  handlers: HookColumnHandlers
): ColumnConfig<HookInventoryRow>[] {
  return [
    {
      id: HOOK_FILTERABLE_FIELDS.FILE_CATEGORY,
      header: 'Type + Event',
      accessor: 'fileCategory',
      width: 220,
      renderCell: ({ row }) => <TypeCell row={row} />,
      sortable: true,
      sortField: 'family',
      sortKey: 'ht',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ],
      statePersistence: {
        urlKey: HOOK_FILTER_URL_KEYS[HOOK_FILTERABLE_FIELDS.FILE_CATEGORY],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 10 },
        dropdown: { width: 280 },
        getQueryKey: params =>
          getHookFilterOptionsQueryKey(
            scope,
            HOOK_FILTERABLE_FIELDS.FILE_CATEGORY,
            params
          ),
        queryFn: params =>
          fetchHookFilterOptions(
            scope,
            HOOK_FILTERABLE_FIELDS.FILE_CATEGORY,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <FileCategoryFilterOption
            value={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: HOOK_FILTERABLE_FIELDS.PLATFORM,
      header: 'IDE/Platform',
      accessor: 'idePlatform',
      width: 150,
      renderCell: ({ row }) => <PlatformCell row={row} />,
      sortable: true,
      sortField: 'platform',
      sortKey: 'hp',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ],
      statePersistence: {
        urlKey: HOOK_FILTER_URL_KEYS[HOOK_FILTERABLE_FIELDS.PLATFORM],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 10 },
        getQueryKey: params =>
          getHookFilterOptionsQueryKey(
            scope,
            HOOK_FILTERABLE_FIELDS.PLATFORM,
            params
          ),
        queryFn: params =>
          fetchHookFilterOptions(
            scope,
            HOOK_FILTERABLE_FIELDS.PLATFORM,
            params
          ),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <PlatformFilterOption value={option.value} isSelected={isSelected} />
        ),
      },
    },
    {
      id: HOOK_FILTERABLE_FIELDS.FILE,
      header: 'File',
      // Sort/display value mirrors FileCell: virtual rows
      // (`vscode-settings://…`) collapse to their friendly label so the
      // column's sort order matches what's rendered (Bugbot KIR-2917).
      accessor: (row): string => getHookFileDisplayLabel(row.filePath),
      width: 240,
      renderCell: ({ row }) => <FileCell row={row} />,
      sortable: true,
      sortField: 'file',
      sortKey: 'hf',
      filterable: true,
      dataType: ColumnDataTypes.STRING,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
        { value: FilterOperators.CONTAINS, label: 'Contains' },
        { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
      ],
      statePersistence: {
        urlKey: HOOK_FILTER_URL_KEYS[HOOK_FILTERABLE_FIELDS.FILE],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 10 },
        dropdown: { width: 380 },
        getQueryKey: params =>
          getHookFilterOptionsQueryKey(
            scope,
            HOOK_FILTERABLE_FIELDS.FILE,
            params
          ),
        queryFn: params =>
          fetchHookFilterOptions(scope, HOOK_FILTERABLE_FIELDS.FILE, params),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <FilePathFilterOption
            filePath={option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
    {
      id: HOOK_FILTERABLE_FIELDS.SUMMARY,
      header: 'Hook Details',
      accessor: 'summary',
      width: 460,
      renderCell: ({ row }) => <SummaryCell row={row} />,
      sortable: true,
      sortField: 'summary',
      sortKey: 'hs',
      filterable: true,
      dataType: ColumnDataTypes.TEXT,
      filterOperators: [
        { value: FilterOperators.CONTAINS, label: 'Contains' },
        { value: FilterOperators.NOT_CONTAINS, label: 'Does not contain' },
        { value: FilterOperators.STARTS_WITH, label: 'Starts with' },
        { value: FilterOperators.ENDS_WITH, label: 'Ends with' },
      ],
      statePersistence: {
        urlKey: HOOK_FILTER_URL_KEYS[HOOK_FILTERABLE_FIELDS.SUMMARY],
      },
    },
    {
      // Users column (KIR-3643): count of unique users the hook is installed
      // for; the click opens the users list. Sortable server-side ('users');
      // not filterable (no server-backed users filter for hooks).
      id: 'users',
      header: 'Users',
      accessor: 'users',
      width: 90,
      renderCell: ({ row }) => (
        <UsersCell row={row} onUsersClick={handlers.onUsersClick} />
      ),
      sortable: true,
      sortField: 'users',
      sortKey: 'hu',
    },
    {
      // Last Seen column (KIR-3643): most-recent detection timestamp.
      id: 'lastSeenAt',
      header: 'Last Seen',
      timestamp: true,
      accessor: 'lastSeenAt',
      renderCell: ({ row }) => <LastSeenCell row={row} />,
      sortable: true,
      sortField: 'lastSeenAt',
      sortKey: 'hls',
    },
    {
      id: HOOK_FILTERABLE_FIELDS.PROJECTS,
      header: 'Projects',
      accessor: 'projects',
      width: 130,
      renderCell: ({ row }) => (
        <ProjectsCell row={row} onProjectsClick={handlers.onProjectsClick} />
      ),
      sortable: true,
      sortField: 'projects',
      sortKey: 'hpr',
      filterable: true,
      dataType: ColumnDataTypes.ENUM,
      filterOperators: [
        { value: FilterOperators.INCLUDES, label: 'Includes' },
        { value: FilterOperators.EXCLUDES, label: 'Excludes' },
      ],
      statePersistence: {
        urlKey: HOOK_FILTER_URL_KEYS[HOOK_FILTERABLE_FIELDS.PROJECTS],
      },
      filterOptions: {
        type: FilterOptionTypes.DYNAMIC,
        pagination: { mode: 'offset', limit: 10 },
        getQueryKey: params =>
          getHookProjectFilterOptionsQueryKey(scope, params),
        queryFn: params => fetchHookProjectFilterOptions(scope, params),
        queryConfig: { staleTime: 2 * 60 * 1000 },
        render: (option, isSelected) => (
          <ProjectFilterOption
            label={option.label ?? option.value}
            isSelected={isSelected}
          />
        ),
      },
    },
  ];
}
